/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useEntity } from './EntityContext';
import { extractPlatformUser } from '../utils/auth0UserUtils';
import { useLoggerWithContext } from '../services/logger/loggerContext';
import { FavoritesManagerModal } from '../components/Favorites/FavoritesManagerModal';
import { type Favorite } from '../services/favoritesStorage';
import type { FavoritesData } from '../services/entityApi';
import { useFavoritesQuery, favoritesQueryKey } from '../hooks/useFavoritesQuery';
import { useFavoritesMutation } from '../hooks/useFavoritesMutation';
import { useResetState } from '../hooks/useResetState';
import { useFeatureFlag, FeatureFlagKey } from '../hooks/useFeatureFlags';

type ManagerMode = 'closed' | 'browse' | 'add';

/**
 * The single owner of favorites *storage*. The sidebar section reads the list and opens the
 * manager; the manager dialog stages edits locally and commits the whole list back via
 * `replaceAll`. Surfaces never touch the API directly, which is what keeps them in sync.
 *
 * `isSaving` and `isSaveError` live here so the modal doesn't need async try/catch logic —
 * it calls `replaceAll` and reads state back from context.
 */
export interface FavoritesContextValue {
  favorites: Favorite[];
  isLoading: boolean;
  isError: boolean;
  isSaving: boolean;
  isSaveError: boolean;
  clearSaveError: () => void;
  /** Commit a new favorites list, replacing the stored one. The manager's Save uses this. */
  replaceAll: (favorites: Favorite[]) => void;
  /** Open the manager for browsing/editing existing favorites (add form collapsed). */
  openManager: () => void;
  /** Open the manager with the add form expanded and prefilled for the current page. */
  openManagerForCurrentPage: () => void;
  /** True when the manager should mount with its add form already expanded. */
  managerStartInAdd: boolean;
  isManagerOpen: boolean;
  closeManager: () => void;
}

const FavoritesContext = createContext<FavoritesContextValue | undefined>(undefined);

interface FavoritesProviderProps {
  readonly children: ReactNode;
}

export function FavoritesProvider({ children }: FavoritesProviderProps) {
  const { user, token } = useAuth();
  const logger = useLoggerWithContext();
  const userUuid = useMemo(() => extractPlatformUser(user)?.uuid, [user]);
  const { activeEntity } = useEntity();
  const entityId = activeEntity?.entityId;
  const queryClient = useQueryClient();

  // Collapses isManagerOpen + managerStartInAdd into one state; resets to closed on entity change.
  const [managerMode, setManagerMode] = useResetState<ManagerMode>('closed', entityId);
  const isManagerOpen = managerMode !== 'closed';
  const managerStartInAdd = managerMode === 'add';

  const [isSaveError, setIsSaveError] = useState(false);
  const clearSaveError = useCallback(() => setIsSaveError(false), []);

  const queryKey = useMemo(() => favoritesQueryKey(userUuid), [userUuid]);
  const favoritesEnabled = useFeatureFlag(FeatureFlagKey.PSP_UI_FAVORITES);

  const {
    data: favorites = [],
    isLoading,
    isError,
  } = useFavoritesQuery({
    userUuid,
    token,
    entityId,
    logger,
    enabled: favoritesEnabled,
  });

  const closeManager = useCallback(() => setManagerMode('closed'), [setManagerMode]);

  const openManager = useCallback(() => {
    logger.info('Favorites manager opened');
    setManagerMode('browse');
  }, [logger, setManagerMode]);

  const openManagerForCurrentPage = useCallback(() => {
    logger.info('Favorites manager opened for current page', {
      pathname: window.location.pathname,
    });
    setManagerMode('add');
  }, [logger, setManagerMode]);

  const mutation = useFavoritesMutation({
    userUuid,
    token,
    entityId,
    queryKey,
    logger,
    onMutateStart: () => setIsSaveError(false),
    onSuccess: closeManager,
    onError: () => setIsSaveError(true),
  });
  const { mutate } = mutation;

  const replaceAll = useCallback(
    (next: Favorite[]) => {
      if (!userUuid || !token || entityId == null) {
        setIsSaveError(true);
        return;
      }
      // Guard against data loss: if the initial fetch hasn't resolved, we don't yet have the
      // full favoritesByEntity map. PUTting now would overwrite all other entities' favorites.
      if (queryClient.getQueryData<FavoritesData>(queryKey) === undefined) {
        setIsSaveError(true);
        return;
      }
      mutate({ next, prev: favorites });
    },
    [mutate, favorites, userUuid, token, entityId, queryClient, queryKey]
  );

  const value = useMemo<FavoritesContextValue>(
    () => ({
      favorites,
      isLoading,
      isError,
      isSaving: mutation.isPending,
      isSaveError,
      clearSaveError,
      replaceAll,
      openManager,
      openManagerForCurrentPage,
      managerStartInAdd,
      isManagerOpen,
      closeManager,
    }),
    [
      favorites,
      isLoading,
      isError,
      mutation.isPending,
      isSaveError,
      clearSaveError,
      replaceAll,
      openManager,
      openManagerForCurrentPage,
      managerStartInAdd,
      isManagerOpen,
      closeManager,
    ]
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
      {/* Mounted on open so it snapshots the current page's title/url at that moment, and so
          its local draft re-initializes from the stored list on every open. */}
      {isManagerOpen && <FavoritesManagerModal open onClose={closeManager} />}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}
